<script>
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import UserForm from '@/components/users/UserForm';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'EditUser',
  components: {
    DashboardLayout,
    UserForm
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Edit User'),
      breadcrumbs: [],
      deleting: false,
      invalid: [],
      isDisabled: false,
      isSaving: false,
      permissions: [],
      statusCode: null,
      user: {}
    };
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.$refs.userForm && this.$refs.userForm.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  created() {
    // If you're editing yourself, bump to the profile page
    if (this.$route.params.userId === this.$store.state.user.id) {
      this.$router.replace({ name: 'EditProfile' });
      return;
    }

    // Fetch data
    Promise.all([
      this.$http.get(`/users/${this.$route.params.userId}`),
      this.$http.get(`/users/${this.$route.params.userId}/permissions`)
    ])
      .then(res => {
        this.user = res[0].data.user;
        this.breadcrumbs = [
          { text: this.$t('Users'), to: { name: 'Users' } },
          { text: this.user.name, to: { name: 'User', params: { userId: this.user.id } } },
          { text: this.$t('Edit') }
        ];
        this.permissions = res[1].data.permissions;
        this.statusCode = 200;

        this.$nextTick(() => {
          this.$refs.userForm.setData({
            name: this.user.name,
            email: this.user.email,
            avatar: this.user.avatar,
            bio: this.user.bio,
            isAdmin: this.user.isAdmin,
            canManageBilling: this.user.canManageBilling,
            canManageSettings: this.user.canManageSettings,
            canManageSites: this.user.canManageSites,
            canManageUsers: this.user.canManageUsers,
            permissions: this.permissions
          });
          this.$refs.userForm.resetCleanState();
        });
      })
      .catch(err => (this.statusCode = err.response.status));
  },
  methods: {
    // Cancels all changes and goes back to the users page
    handleCancel() {
      this.$router.push({ name: 'Users' });
    },

    // Deletes the user
    handleDelete() {
      this.$confirm(this.$t('Are you sure you want to do this?'), {
        title: this.$t('Delete'),
        confirmButtonText: this.$t('Delete This User'),
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('Cancel')
      }).then(() => {
        this.deleting = true;
        this.$http
          .delete(`/users/${this.user.id}`)
          .then(() => {
            this.$refs.userForm.resetCleanState();
            this.$router.push({ name: 'Users' });
          })
          .catch(err => {
            this.$notify({
              type: 'error',
              title: this.$t('Delete'),
              message: getErrorMessage(err.response.data.code)
            });
          })
          .finally(() => (this.deleting = false));

        return null;
      });
    },

    // Saves the user
    handleSave() {
      const data = this.$refs.userForm.getData();
      this.isSaving = true;

      Promise.all([
        this.$http.put(`/users/${this.user.id}`, {
          name: data.name,
          email: data.email,
          avatar: data.avatar,
          bio: data.bio,
          isAdmin: data.isAdmin,
          canManageBilling: data.canManageBilling,
          canManageSettings: data.canManageSettings,
          canManageSites: data.canManageSites,
          canManageUsers: data.canManageUsers,
          permissions: data.permissions
        }),
        this.$http.put(`/users/${this.user.id}/permissions`, { permissions: data.permissions })
      ])
        .then(() => {
          this.invalid = [];
          this.$refs.userForm.resetCleanState();
          this.$router.push({ name: 'User', params: { userId: this.user.id } });
        })
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isSaving = false));
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- User form -->
    <user-form
      ref="userForm"
      :deleting="deleting"
      :disabled="isDisabled"
      :saving="isSaving"
      :invalid="invalid"
      @delete="handleDelete"
      @cancel="handleCancel"
      @submit="handleSave"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

// No styles yet
</style>
